import React, { createContext, useContext, useEffect, useState } from "react";
import { apiUrl } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const fetchMe = async () => {
    try {
      setAuthLoading(true);

      // First check localStorage for existing auth
      const storedUser = localStorage.getItem("werkora_user");
      const token = localStorage.getItem("token");

      // No token => no point pinging /auth/me. Avoids 401/503 console noise.
      if (!storedUser || !token) {
        setUser(null);
        return null;
      }

      let userData = null;
      try {
        userData = JSON.parse(storedUser);
      } catch (e) {
        // Stored user is corrupted; clear and bail out
        console.warn("Corrupted werkora_user in localStorage, clearing.");
        localStorage.removeItem("werkora_user");
        setUser(null);
        return null;
      }

      // Optimistically set the user from localStorage so guards work fast
      setUser(userData);

      // Verify with backend (best-effort). Failure here should NOT log out the
      // user — they can still navigate; the next protected API call will 401
      // and the app will recover gracefully.
      try {
        const res = await fetch(apiUrl(`/auth/me?user_id=${userData.id}`), {
          method: "GET",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.user) {
            setUser(data.user);
            return data.user;
          }
        }
      } catch (netErr) {
        // Silent: keep optimistic user from localStorage
      }
      return userData;
    } catch (e) {
      console.error("Auth check error:", e);
      setUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("werkora_user", JSON.stringify(userData));
    localStorage.setItem("token", userData.id);
    localStorage.setItem("role", userData.role);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("werkora_user");
    localStorage.removeItem("token");
    localStorage.removeItem("role");
  };

  useEffect(() => {
    fetchMe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, authLoading, fetchMe, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  // Return safe defaults if context is null (not wrapped in provider)
  if (!context) {
    return { user: null, authLoading: true, setUser: () => {}, fetchMe: () => {}, login: () => {}, logout: () => {} };
  }
  return context;
}
